import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useNavigate } from 'react-router-dom';
import { NETWORK_DOMAIN } from '../utils/constants';
import { Equipment } from '../types/schema';
import { useAppContext } from '../contexts/AppContext';
import { useEffect, useState, type ReactNode } from 'react';
import '../css/QRCodes.css';
import { BackButton, Btn, Card } from '../components/ui';
import CardHeader from '../components/ui/headers/CardHeader';
import { IconClose, IconPrint } from '@icons';
import { TotalEquipIQLogoIcon } from '../components/ui/icons-logos';
import { EquipIQQRCode } from '../components/EquipIQQRCode';

const PRINT_COLS = 5;
const PRINT_SLOTS = PRINT_COLS * PRINT_COLS;
const MOBILE_COL_OPTIONS = [1, 2] as const;
const DESKTOP_COL_OPTIONS = [2, 3, 4, 5] as const;
type PreviewCols = 1 | 2 | 3 | 4 | 5;

type GridCell = Equipment | null;
type GridLayout = GridCell[][];

const createEmptyGrid = (cols: number): GridLayout => {
    const rows = Math.ceil(PRINT_SLOTS / cols);
    return Array.from({ length: rows }, () => Array<GridCell>(cols).fill(null));
};

const remapGrid = (prev: GridLayout, cols: number): GridLayout => {
    const items = prev.flat().filter((cell): cell is Equipment => cell != null);
    const next = createEmptyGrid(cols);
    items.slice(0, next.length * cols).forEach((item, index) => {
        next[Math.floor(index / cols)][index % cols] = item;
    });
    return next;
};

const isMobileViewport = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

const getDefaultPreviewCols = (): PreviewCols => (isMobileViewport() ? 1 : 5);

/** HTML5 drag does not work on touch; use TouchBackend on touch / coarse-pointer devices. */
const getDndConfig = () => {
    const isTouch =
        typeof window !== 'undefined' &&
        (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);

  if (isTouch) {
    return {
      isTouch: true as const,
      backend: TouchBackend,
      options: {
        enableMouseEvents: true,
        // Start drag immediately — a long delay made Chrome feel like you
        // had to wait for the card to dim before it would move.
        delayTouchStart: 0,
        touchSlop: 20,
        ignoreContextMenu: true,
      },
    };
  }

    return { isTouch: false as const, backend: HTML5Backend, options: undefined };
};

export const QRCodes = () => {
    const navigate = useNavigate();
    const equipmentList = JSON.parse(sessionStorage.getItem('equipmentList') || '[]') as Equipment[];
    const { isSideNavOpen, setIsSideNavOpen, initialSideNavOpenState, setInitialSideNavOpenState } = useAppContext();
    const [shouldPrintQR, setShouldPrintQR] = useState(false);
    const [previewCols, setPreviewCols] = useState<PreviewCols>(getDefaultPreviewCols);
    const [gridLayout, setGridLayout] = useState<GridLayout>(() => createEmptyGrid(getDefaultPreviewCols()));
    const [dndConfig] = useState(getDndConfig);
    const [isMobile] = useState(isMobileViewport);
    const colOptions = isMobile ? MOBILE_COL_OPTIONS : DESKTOP_COL_OPTIONS;

    useEffect(() => {
        if (!shouldPrintQR) return;

        let finished = false;
        const sheet = document.querySelector<HTMLElement>('.qr-code-page.viewPrint');
        sheet?.classList.add('is-print-ready');

        const finish = () => {
            if (finished) return;
            finished = true;
            sheet?.classList.remove('is-print-ready');
            setShouldPrintQR(false);
            setIsSideNavOpen(initialSideNavOpenState);
            window.removeEventListener('afterprint', finish);
        };

        window.addEventListener('afterprint', finish);

        // Mobile Chrome won't rasterize SVGs that lived at left:-10000px.
        // Show the sheet, wait a paint+tick, then open the print dialog.
        const printTimer = window.setTimeout(() => {
            window.print();
            // Some mobile browsers never fire afterprint
            window.setTimeout(finish, 1500);
        }, 200);

        return () => {
            window.clearTimeout(printTimer);
            window.removeEventListener('afterprint', finish);
            sheet?.classList.remove('is-print-ready');
        };
    }, [shouldPrintQR, initialSideNavOpenState, setIsSideNavOpen]);

    const handlePreviewColsChange = (cols: PreviewCols) => {
        if (cols === previewCols) return;
        setPreviewCols(cols);
        setGridLayout((prev) => remapGrid(prev, cols));
    };

    // Handle dropping a QR code onto the grid
    const handleDrop = (item: { id: string }, x: number, y: number) => {
        const newLayout = gridLayout.map((row) => [...row]);
        const equipment = equipmentList.find((eq) => eq.id === item.id);
        if (equipment) {
            newLayout[y][x] = equipment;
            setGridLayout(newLayout);
        }
    };

    // Handle clearing a cell
    const handleClearCell = (x: number, y: number) => {
        const newLayout = gridLayout.map((row) => [...row]);
        newLayout[y][x] = null;
        setGridLayout(newLayout);
    };

    /** Print the preview grid at the selected column count; drop trailing empty rows. */
    const printRows: GridLayout = (() => {
        let lastFilledRow = -1;
        gridLayout.forEach((row, y) => {
            if (row.some((cell) => cell != null)) lastFilledRow = y;
        });
        if (lastFilledRow < 0) {
            return [Array<GridCell>(previewCols).fill(null)];
        }
        return gridLayout.slice(0, lastFilledRow + 1);
    })();

    const printQrSize = ({ 1: 260, 2: 220, 3: 170, 4: 140, 5: 125 } as const)[previewCols];

    const DraggableQRCard = ({ equipment }: { equipment: Equipment }) => {
        const [isPressing, setIsPressing] = useState(false);
        const [{ isDragging }, drag] = useDrag(() => ({
            type: 'qrcode',
            item: { id: equipment.id, name: equipment.name },
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
        }));

        const clearPress = () => setIsPressing(false);

        return (
            <div
                ref={drag}
                className={[
                    'qr-codes-list-item',
                    isPressing ? 'is-pressing' : '',
                    isDragging ? 'is-dragging' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
                onTouchStart={() => setIsPressing(true)}
                onTouchEnd={clearPress}
                onTouchCancel={clearPress}
                onMouseDown={() => setIsPressing(true)}
                onMouseUp={clearPress}
                onMouseLeave={clearPress}
            >
                <div className="qr-code-header">
                    <span className="equipment-name">{equipment.name}</span>
                    <TotalEquipIQLogoIcon iconClasses="logo-qr" />
                </div>
                <EquipIQQRCode value={`${NETWORK_DOMAIN}/inspect/${equipment.id}`} size={125} className="qr-code" />
            </div>
        );
    };

    // Define the droppable grid cell
    const DroppableCell = ({
        x,
        y,
        children,
        onDrop,
    }: {
        x: number;
        y: number;
        children?: ReactNode;
        onDrop: (item: { id: string }, x: number, y: number) => void;
    }) => {
        const [{ isOver }, drop] = useDrop(() => ({
            accept: 'qrcode',
            drop: (item: { id: string }) => onDrop(item, x, y),
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
            }),
        }));

        return (
            <div ref={drop} className={`qr-droppable-cell${isOver ? ' is-over' : ''}`}>
                {children}
            </div>
        );
    };

    return (
        <>
            <DndProvider backend={dndConfig.backend} options={dndConfig.options}>
                <div className="page-container hide-from-printer">
                    <div className="qr-codes-toolbar">
                        <BackButton
                            onClick={() => {
                                sessionStorage.removeItem('equipmentList');
                                navigate('/equipment');
                            }}
                        />
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText="Print"
                            buttonIcon={<IconPrint />}
                            buttonIconPosition="left"
                            ariaLabel="Print QR codes"
                            buttonOnClick={() => {
                                setInitialSideNavOpenState(isSideNavOpen);
                                setIsSideNavOpen(false);
                                setShouldPrintQR(true);
                            }}
                        />
                    </div>

                    <Card id="rdp" variant="card-lg" className="qr-code-card">
                        <CardHeader as="h2" title="Drag QR Codes to Layout Preview" paddingVariant="pad-md" />
                        <div className="qr-code-card-body">
                            <div className="qr-codes">
                                <h3 className="qr-codes-section-title">Available QR Codes</h3>
                {dndConfig.isTouch && (
                  <p className="qr-codes-touch-hint" role="note">
                    Drag a QR code into the layout below. Swipe between cards to scroll the list.
                  </p>
                )}
                                <div className="qr-codes-list">
                                    {equipmentList.map((equipment) => (
                                        <DraggableQRCard key={equipment.id} equipment={equipment} />
                                    ))}
                                </div>
                            </div>

                            <div className="qr-codes-preview">
                                <div className="qr-codes-preview-header">
                                    <h3 className="qr-codes-section-title">Layout Preview</h3>
                                    <div className="qr-codes-cols-control" role="group" aria-label="Columns per row">
                                        <span className="qr-codes-cols-label" id="qr-cols-label">
                                            Columns
                                        </span>
                    {colOptions.map((cols) => (
                      <Btn
                        key={cols}
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonText={String(cols)}
                        buttonClasses={previewCols === cols ? 'active' : undefined}
                        ariaLabel={`${cols} column${cols === 1 ? '' : 's'} per row`}
                        buttonAriaProps={{
                          'aria-pressed': previewCols === cols,
                          'aria-describedby': 'qr-cols-label',
                        }}
                        buttonOnClick={() => handlePreviewColsChange(cols)}
                      />
                    ))}
                                    </div>
                                </div>
                                <div className="qr-code-grid preview-grid" data-cols={previewCols}>
                                    {gridLayout.map((row, y) =>
                                        row.map((cell, x) => (
                                            <DroppableCell key={`${x}-${y}`} x={x} y={y} onDrop={handleDrop}>
                                                {cell && (
                                                    <div className="qr-code-item">
                                                        <div className="qr-code-header">
                                                            <span className="equipment-name">{cell.name}</span>
                                                            <TotalEquipIQLogoIcon iconClasses="logo-qr" />
                                                        </div>
                                                        {window.location.hostname === 'localhost' ||
                                                        window.location.hostname.startsWith('192.168.') ? (
                                                            <a
                                                                href={`${NETWORK_DOMAIN}/inspect/${cell.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ cursor: 'pointer', display: 'block' }}
                                                            >
                                                                <EquipIQQRCode
                                                                    value={`${NETWORK_DOMAIN}/inspect/${cell.id}`}
                                                                    size={125}
                                                                    className="qr-code"
                                                                />
                                                            </a>
                                                        ) : (
                                                            <EquipIQQRCode
                                                                value={`${NETWORK_DOMAIN}/inspect/${cell.id}`}
                                                                size={125}
                                                                className="qr-code"
                                                            />
                                                        )}
                                                        <Btn
                                                            variant="icon"
                                                            buttonSize="sm"
                                                            buttonIcon={<IconClose />}
                                                            ariaLabel={`Remove ${cell.name} from layout`}
                                                            buttonOnClick={() => handleClearCell(x, y)}
                                                        />
                                                    </div>
                                                )}
                                            </DroppableCell>
                                        )),
                                    )}
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Print uses the same column count as Layout Preview */}
                <div className="qr-code-page viewPrint">
                    <div className="qr-code-grid" data-cols={previewCols}>
                        {printRows.flat().map((equipment, index) =>
                            equipment ? (
                                <div className="qr-code-item" key={`print-${index}`}>
                                    <div className="qr-code-header">
                                        <span className="equipment-name">{equipment.name}</span>
                                        <TotalEquipIQLogoIcon iconClasses="logo-qr" />
                                    </div>
                                    <EquipIQQRCode
                                        value={`${NETWORK_DOMAIN}/inspect/${equipment.id}`}
                                        size={printQrSize}
                                        className="qr-code"
                                    />
                                </div>
                            ) : (
                                <div className="qr-code-item empty" key={`print-empty-${index}`}></div>
                            ),
                        )}
                    </div>
                </div>
            </DndProvider>
        </>
    );
};
